import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TourPackage } from "@/hooks/useTourPackages";

interface PackageQuantity {
  packageId: string;
  quantity: number;
}

interface PackageSelectorProps {
  packages: TourPackage[];
  onSelectionChange: (selections: PackageQuantity[], totalPrice: number) => void;
}

export const PackageSelector: React.FC<PackageSelectorProps> = ({ 
  packages, 
  onSelectionChange 
}) => {
  const [selections, setSelections] = useState<PackageQuantity[]>([]);

  const updateQuantity = (packageId: string, change: number) => {
    const newSelections = [...selections];
    const existingIndex = newSelections.findIndex(s => s.packageId === packageId);
    
    if (existingIndex >= 0) {
      const newQuantity = Math.max(0, newSelections[existingIndex].quantity + change);
      if (newQuantity === 0) {
        newSelections.splice(existingIndex, 1);
      } else {
        newSelections[existingIndex].quantity = newQuantity;
      }
    } else if (change > 0) {
      newSelections.push({ packageId, quantity: 1 });
    }
    
    setSelections(newSelections);
    
    // Calculate total price
    const totalPrice = newSelections.reduce((total, selection) => {
      const pkg = packages.find(p => p.id === selection.packageId);
      return total + (pkg ? pkg.package_price * selection.quantity : 0);
    }, 0);
    
    onSelectionChange(newSelections, totalPrice);
  };

  const getQuantity = (packageId: string) => {
    const selection = selections.find(s => s.packageId === packageId);
    return selection ? selection.quantity : 0;
  };

  const getTotalPersons = () => {
    return selections.reduce((total, selection) => {
      const pkg = packages.find(p => p.id === selection.packageId);
      return total + (pkg ? pkg.persons_included * selection.quantity : 0);
    }, 0);
  };

  const getTotalPrice = () => {
    return selections.reduce((total, selection) => {
      const pkg = packages.find(p => p.id === selection.packageId);
      return total + (pkg ? pkg.package_price * selection.quantity : 0);
    }, 0);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {packages.map((pkg) => {
          const quantity = getQuantity(pkg.id);
          
          return (
            <Card key={pkg.id} className="border-2 hover:border-primary/20 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{pkg.package_name}</CardTitle>
                  <Badge variant="secondary" className="text-lg font-bold">
                    ${pkg.package_price}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Includes: {pkg.persons_included} persons</span>
                  </div>
                  
                  {pkg.description && (
                    <p className="text-sm text-muted-foreground">{pkg.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(pkg.id, -1)}
                        disabled={quantity === 0}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      
                      <span className="min-w-[3rem] text-center font-semibold">
                        {quantity}
                      </span>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(pkg.id, 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {quantity > 0 && (
                      <div className="text-right">
                        <div className="font-semibold">
                          ${(pkg.package_price * quantity).toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {pkg.persons_included * quantity} persons
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {selections.length > 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Total Selection</h3>
                <p className="text-sm text-muted-foreground">
                  {getTotalPersons()} persons total
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary">
                  ${getTotalPrice().toFixed(2)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};